import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Key, Users, CheckCircle, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

export default function SSOConfiguration() {
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [samlConfig, setSamlConfig] = useState({
    entityId: '',
    ssoUrl: '',
    certificate: '',
    attributeMapping: {
      email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
      firstName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
      lastName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
      department: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/department'
    }
  });

  const [testResults, setTestResults] = useState<{
    status: 'success' | 'error' | 'pending' | null;
    message: string;
  }>({ status: null, message: '' });

  const handleSaveConfiguration = async () => {
    try {
      // In a real implementation, this would call an API
      toast.success('SSO configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save SSO configuration');
    }
  };

  const handleTestConnection = async () => {
    setTestResults({ status: 'pending', message: 'Testing connection...' });
    
    // Simulate API call
    setTimeout(() => {
      setTestResults({ 
        status: 'success', 
        message: 'Successfully connected to Microsoft Azure AD. MFA requirements detected and configured.' 
      });
    }, 2000);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">SSO & SAML Configuration</h1>
        <p className="text-muted-foreground">Configure Single Sign-On integration with your district's Microsoft Azure AD</p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          SSO integration requires coordination with your district's IT department to configure the SAML application in Azure AD.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="configuration" className="space-y-6">
        <TabsList>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="testing">Testing & Validation</TabsTrigger>
          <TabsTrigger value="user-provisioning">User Provisioning</TabsTrigger>
        </TabsList>

        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    SSO Status
                  </CardTitle>
                  <CardDescription>Enable or disable Single Sign-On for your district</CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={ssoEnabled}
                    onCheckedChange={setSsoEnabled}
                  />
                  <Label htmlFor="sso-enabled">
                    {ssoEnabled ? 'Enabled' : 'Disabled'}
                  </Label>
                </div>
              </div>
            </CardHeader>
            {ssoEnabled && (
              <CardContent className="space-y-4">
                <Badge variant="outline" className="w-fit">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Microsoft Azure AD Integration
                </Badge>
              </CardContent>
            )}
          </Card>

          {ssoEnabled && (
            <Card>
              <CardHeader>
                <CardTitle>SAML Configuration</CardTitle>
                <CardDescription>
                  Configure the SAML settings for your Microsoft Azure AD integration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="entityId">Entity ID / Identifier</Label>
                    <Input
                      id="entityId"
                      value={samlConfig.entityId}
                      onChange={(e) => setSamlConfig({...samlConfig, entityId: e.target.value})}
                      placeholder="https://your-district.edugogy.com/saml/metadata"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      This identifier will be used in your Azure AD SAML app configuration
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="ssoUrl">Single Sign-On URL</Label>
                    <Input
                      id="ssoUrl"
                      value={samlConfig.ssoUrl}
                      onChange={(e) => setSamlConfig({...samlConfig, ssoUrl: e.target.value})}
                      placeholder="https://login.microsoftonline.com/your-tenant-id/saml2"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      The SAML SSO endpoint URL from your Azure AD application
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="certificate">X.509 Certificate</Label>
                    <textarea
                      id="certificate"
                      className="w-full h-32 p-3 border rounded-md text-sm font-mono"
                      value={samlConfig.certificate}
                      onChange={(e) => setSamlConfig({...samlConfig, certificate: e.target.value})}
                      placeholder="-----BEGIN CERTIFICATE-----
MIIDBTCCAe2gAwIBAgIQYdVhWr7...
-----END CERTIFICATE-----"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      The signing certificate from your Azure AD SAML application
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Attribute Mapping</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Email Address</Label>
                      <Input
                        value={samlConfig.attributeMapping.email}
                        onChange={(e) => setSamlConfig({
                          ...samlConfig,
                          attributeMapping: {...samlConfig.attributeMapping, email: e.target.value}
                        })}
                      />
                    </div>
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={samlConfig.attributeMapping.firstName}
                        onChange={(e) => setSamlConfig({
                          ...samlConfig,
                          attributeMapping: {...samlConfig.attributeMapping, firstName: e.target.value}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>  
                      <Input
                        value={samlConfig.attributeMapping.lastName}
                        onChange={(e) => setSamlConfig({
                          ...samlConfig,
                          attributeMapping: {...samlConfig.attributeMapping, lastName: e.target.value}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Department</Label>
                      <Input
                        value={samlConfig.attributeMapping.department}
                        onChange={(e) => setSamlConfig({
                          ...samlConfig,
                          attributeMapping: {...samlConfig.attributeMapping, department: e.target.value}
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSaveConfiguration}>
                    Save Configuration
                  </Button>
                  <Button variant="outline">
                    Download Metadata
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="testing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Connection Testing
              </CardTitle>
              <CardDescription>
                Test your SAML configuration and validate the connection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleTestConnection} disabled={testResults.status === 'pending'}>
                {testResults.status === 'pending' ? 'Testing...' : 'Test SAML Connection'}
              </Button>

              {testResults.status && (
                <Alert className={testResults.status === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                  {testResults.status === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertDescription className={testResults.status === 'success' ? 'text-green-800' : 'text-red-800'}>
                    {testResults.message}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                <h4 className="font-semibold">Multi-Factor Authentication Support</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Microsoft Authenticator App</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">SMS/Phone Call Verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Hardware Token Support</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Conditional Access Policies</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="user-provisioning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Automatic User Provisioning
              </CardTitle>
              <CardDescription>
                Configure automatic user account creation and updates from Azure AD
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Just-In-Time Provisioning</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      Automatically create user accounts when they first sign in via SSO
                    </p>
                    <Switch defaultChecked />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Automatic Role Assignment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      Assign roles based on Azure AD group membership
                    </p>
                    <Switch defaultChecked />
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold">Role Mapping Configuration</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">District Administrators</span>
                      <p className="text-sm text-muted-foreground">Azure AD Group: EdugoPD-Admins</p>
                    </div>
                    <Badge>Admin Role</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">PD Coordinators</span>
                      <p className="text-sm text-muted-foreground">Azure AD Group: EdugoPD-Coordinators</p>
                    </div>
                    <Badge variant="secondary">Coordinator Role</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">All Teachers</span>
                      <p className="text-sm text-muted-foreground">Azure AD Group: District-Teachers</p>
                    </div>
                    <Badge variant="outline">User Role</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}